import React, { useState, useEffect, useContext } from "react";
import "../../styles/projects/ConvertToProject.css";
import { useAuth } from "../Authentication/AuthContext";
import api from "../../api/axiosConfig";
import { ProjectContext } from "../../App";
import { IoMdClose } from "react-icons/io";
import { ToastContainer, toast } from "react-toastify";
import { MultiSelect } from "react-multi-select-component";

const ConvertToProject = ({ estimateDetails, goBack, setLoading, loading }) => {
  // Log the received prop for debugging
  const { userDetails } = useAuth();
  const userID = userDetails.id;
  const [taskMapping, setTaskMapping] = useState({});
  const [taskToConvert, setTasktoConvert] = useState([]);
  const { isNavBarOpen } = useContext(ProjectContext);
  const [estimate, setEstimate] = useState(estimateDetails || { phases: [] });
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [templateSelections, setTemplateSelections] = useState({});
  const [selectedTemplates, setSelectedTemplates] = useState({});
  const [selectedTaskMappings, setSelectedTaskMappings] = useState({});
  useEffect(() => {
    const defaultMappings = {};
    estimate.phases.forEach((phase) => {
      phase.taskTemplates.forEach((template) => {
        template.estimateTasks.forEach((task) => {
          // If you have a field like task.mapped_to
          if (task.mapped_to) {
            defaultMappings[task.id] = task.mapped_to;
          }
        });
      });
    });
    setSelectedTaskMappings(defaultMappings);
  }, [estimate]);

  const [tasks, setTasks] = useState({});
  const [users, setUsers] = useState({});
  const [manager, setManager] = useState([]);
  const calculatePlannedHoursGroupedByPhaseTemplateTask = (tasks) => {
    const groupedByPhase = {};

    // Step 1: Group by phase_name
    tasks.forEach((task) => {
      const { phase_name, phaseno, template_name, task_name, planned_hours } =
        task;

      // Parse planned_hours into seconds for summation
      const [hours, minutes, seconds] = planned_hours.split(":").map(Number);
      const totalSeconds = hours * 3600 + minutes * 60 + seconds;

      if (!groupedByPhase[phase_name]) {
        groupedByPhase[phase_name] = { phaseno, templates: {} };
      }

      // Step 2: Group by template_name within each phase_name
      if (!groupedByPhase[phase_name].templates[template_name]) {
        groupedByPhase[phase_name].templates[template_name] = {};
      }

      // Step 3: Group by task_name within each template_name
      if (!groupedByPhase[phase_name].templates[template_name][task_name]) {
        groupedByPhase[phase_name].templates[template_name][task_name] = 0;
      }

      groupedByPhase[phase_name].templates[template_name][task_name] +=
        totalSeconds;
    });

    // Step 4: Convert the grouped data back to a structured format
    const result = Object.entries(groupedByPhase).map(
      ([phase_name, { phaseno, templates }]) => {
        const templateSummaries = Object.entries(templates).map(
          ([template_name, tasks]) => {
            const taskSummaries = Object.entries(tasks).map(
              ([task_name, totalSeconds]) => {
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;

                return {
                  task_name,
                  planned_hours: `${String(hours).padStart(2, "0")}:${String(
                    minutes
                  ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
                };
              }
            );

            return {
              template_name,
              tasks: taskSummaries,
            };
          }
        );

        return {
          phase_name,
          phaseno, // Added phase number here
          templates: templateSummaries,
        };
      }
    );

    return result;
  };

  const groupedAndSummedTasks =
    calculatePlannedHoursGroupedByPhaseTemplateTask(taskToConvert);

  // Update estimate state when selectedEstimateDetails changes
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userres = await api.get("/api/users");
        setUsers(userres.data.filter((member) => member.status !== "Inactive"));
      } catch (error) {
        console.error("Error fetching Users:", error);
      }
    };
    if (estimateDetails) {
      setEstimate(estimateDetails);
    }
    fetchUsers();
  }, [estimateDetails]);

  useEffect(() => {
    const fetchTaskTemplates = async () => {
      try {
        const response = await api.get("/api/tasktemplates"); // Adjust API URL
        setTaskTemplates(response.data);

        // Initialize templateSelections dynamically
        const initialSelections = {};
        response.data.forEach((template) => {
          initialSelections[template.id] = []; // Start with an empty array for each template
        });
        setTemplateSelections(initialSelections);
      } catch (error) {
        console.error("Error fetching task templates:", error);
      }
    };

    fetchTaskTemplates();
  }, []);

  const handleTaskSelection = (
    phasename,
    phaseno,
    templateId,
    templateName,
    taskId,
    plannedhours,
    selectedTaskName
  ) => {
    const newTask = {
      phase_name: phasename,
      phaseno: phaseno,
      template_id: templateId,
      template_name: templateName,
      task_id: taskId,
      task_name: selectedTaskName,
      planned_hours: plannedhours,
    };

    setTasktoConvert((prev) => {
      // Check if the task already exists in the array
      const taskExists = prev.some(
        (task) => task.template_id === templateId && task.task_id === taskId
      );

      if (taskExists) {
        // Update the existing task
        return prev.map((task) =>
          task.template_id === templateId && task.task_id === taskId
            ? newTask
            : task
        );
      } else {
        // Add the new task to the array
        return [...prev, newTask];
      }
    });
  };

  const handleTemplateSelection = async (
    templateId,
    rowId,
    selectedTemplateId
  ) => {
    if (!manager.length > 0) {
      setSelectedTemplates([]);
      toast.error("Select Manager", {
        position: "top-center", // Use position as a string
        autoClose: 2000,
      });
      return;
    }
    setSelectedTemplates((prev) => ({
      ...prev,
      [rowId]: selectedTemplateId,
    }));

    try {
      // Fetch tasks for the selected template
      const response = await api.get(`/api/tasks/${selectedTemplateId}`);
      setTasks((prev) => ({
        ...prev,
        [rowId]: response.data, // Store tasks for the corresponding row
      }));
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let payload;
    try {
      if (!tasks || Object.keys(tasks).length === 0) {
        toast.error(
          "Tasks are missing or empty. Please ensure tasks are loaded.",
          {
            position: "bottom-center",
            autoClose: 2000,
          }
        );
        return; // 🔥 Fix: Stop function execution
      }

      const validTasks = [];
      const allTasks = Object.values(tasks).flat();

      estimate.phases.forEach((phase) => {
        phase.taskTemplates.forEach((template) => {
          template.estimateTasks.forEach((task) => {
            const mappedTaskId = taskMapping[`${template.id}-${task.id}`];
            const mappedTask = allTasks.find(
              (t) => t.id === Number(mappedTaskId)
            );

            if (mappedTask) {
              validTasks.push({
                taskId: task.id,
                mappedTaskId,
                taskName: mappedTask.name,
              });
            }
          });
        });
      });

      payload = {
        estimateDetails: estimate,
        taskMapping: validTasks,
        selectedTemplates,
        managerId: manager,
        groupedAndSummedTasks,
        convertedBy: userID,
      };

      const response = await api.post("/api/convertToProject", payload);

      if (response.status === 200) {
        // 🔥 Fix: Clear state only after success
        setManager([]);
        setSelectedTemplates([]);
        goBack();
      } else {
        setTimeout(() => {
          toast.error("Failed to convert project.", {
            position: "bottom-center",
            autoClose: 2000,
          });
        }, 1000);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        const userConfirmed = window.confirm(`${error.response.data.message}`);

        if (userConfirmed) {
          try {
            await api.post("/api/convertToProject", {
              ...payload,
              forceMerge: true,
            });
            setManager([]);
            setSelectedTemplates([]);
            goBack();
          } catch (retryError) {
            toast.error(
              retryError.response?.data?.message || "Force merge failed.",
              {
                position: "bottom-center",
                autoClose: 2000,
              }
            );
            console.error("Retry error:", retryError);
          }
        }
      } else {
        toast.error(error.response?.data?.message || "Something went wrong.", {
          position: "bottom-center",
          autoClose: 2000,
        });
      }
      console.error("Error in handleSubmit:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    estimate.phases.forEach((phase) => {
      phase.taskTemplates.forEach((template) => {
        template.estimateTasks.forEach((estimateTask) => {
          const alreadyMapped = selectedTaskMappings[estimateTask.id];
          if (alreadyMapped) return; // skip if already mapped

          // Try to match using task_category or code
          const matchedTask = tasks[template.id]?.find(
            (t) =>
              t.name === estimateTask.task_category || // match by category
              t.code === estimateTask.code // or match by code, if exists
          );

          if (matchedTask) {
            const matchedTaskName = matchedTask.name;

            setSelectedTaskMappings((prev) => ({
              ...prev,
              [estimateTask.id]: matchedTaskName,
            }));

            handleTaskSelection(
              phase.phase_name,
              phase.phase_number,
              template.id,
              template.template_name,
              estimateTask.id,
              estimateTask.planned_hours,
              matchedTaskName
            );
          }
        });
      });
    });
  }, [estimate, tasks]);

  // Check if estimate or phases are missing
  if (!estimate.phases || estimate.phases.length === 0) {
    return <p>No estimate details available.</p>;
  }

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <span className="loading-animation">LOADING...</span>
      </div>
    );
  }

  return (
    <div
      className={
        isNavBarOpen ? "full-page-container" : "full-page-container-close"
      }
    >
      <div className="page-header">
        <div
          className={
            isNavBarOpen
              ? "convert-to-project-header"
              : "convert-to-project-header-close"
          }
        >
          <h3
            style={{
              margin: "0px 20px 0px 0px",
              fontSize: "14px",
              fontWeight: "normal",
            }}
          >
            Estimate No : {estimate.estimationno}
          </h3>
          <h3
            style={{
              margin: "0px 20px 0px 0px",
              fontSize: "14px",
              fontWeight: "normal",
            }}
          >
            Project : {estimate.projectname}
          </h3>
          <h3
            style={{
              margin: "0px 20px 0px 0px",
              fontSize: "14px",
              fontWeight: "normal",
            }}
          >
            Project Manager :{" "}
          </h3>
          {users.length > 0 && (
            <MultiSelect
              value={manager}
              onChange={(e) => setManager(e)}
              options={users
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((user) => ({
                  label: user.name,
                  value: String(user.id),
                }))}
            />
          )}
        </div>
        <h1 style={{ marginTop: "-0.5%", fontSize: "15px" }}>
          Convert Estimate to Project
        </h1>
        <IoMdClose
          className={
            isNavBarOpen
              ? "estimate-conver-close"
              : "estimate-conver-close-close"
          }
          onClick={goBack}
        />
      </div>
      <div
        className={
          isNavBarOpen
            ? "convert-to-project-table-con"
            : "convert-to-project-table-con-close"
        }
      >
        {estimate.phases.map((phase) => (
          <div key={phase.id} className="phase-section">
            <h3 style={{ fontSize: "14px", fontWeight: "normal" }}>
              {phase.phase_name}
            </h3>
            {phase.taskTemplates.map((template) => (
              <div key={template.id} className="template-section">
                {
                  <div>
                    <h3 style={{ fontSize: "14px", fontWeight: "normal" }}>
                      Select Task template for mapping
                    </h3>
                    <select
                      className="estimate-select-tasktemplate"
                      value={selectedTemplates[template.id] || ""}
                      onChange={(e) =>
                        handleTemplateSelection(
                          template.id,
                          template.id,
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select TaskTemplate</option>
                      {taskTemplates.map((templates) => (
                        <option key={templates.id} value={templates.id}>
                          {templates.name}
                        </option>
                      ))}
                    </select>
                  </div>
                }
                <table className="task-table">
                  <thead>
                    <tr>
                      <th className="task-name-estimate">Task Name</th>
                      <th
                        className="task-name-estimate"
                        style={{ whiteSpace: "nowrap", width: "80px" }}
                      >
                        Planned Hrs
                      </th>
                      <th
                        className="task-name-estimate"
                        style={{ width: "239px" }}
                      >
                        Map to Task
                      </th>
                      <th style={{ width: "640px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {template.estimateTasks
                      .sort((a, b) => a.code.localeCompare(b.code))
                      .map((task) => (
                        <tr key={task.id}>
                          <td>{task.task_name}</td>
                          <td style={{ textAlign: "center", width: "80px" }}>
                            {task.planned_hours.substring(0, 5)}
                          </td>
                          <td>
                            {
                              <select
                                className="estimate-table-dropdown"
                                value={
                                  selectedTaskMappings[task.id] ||
                                  task.task_category ||
                                  ""
                                }
                                onChange={(e) => {
                                  const selectedTaskName = e.target.value;
                                  setSelectedTaskMappings((prev) => ({
                                    ...prev,
                                    [task.id]: selectedTaskName, // track by task ID
                                  }));
                                  handleTaskSelection(
                                    phase.phase_name,
                                    phase.phase_number,
                                    template.id,
                                    template.template_name,
                                    task.id,
                                    task.planned_hours,
                                    selectedTaskName // send selected task name
                                  );
                                }}
                              >
                                <option value="">Select Task</option>
                                {tasks[template.id] &&
                                  tasks[template.id].map((taskOption) => (
                                    <option
                                      key={taskOption.id}
                                      value={taskOption.name}
                                    >
                                      {taskOption.name}
                                    </option>
                                  ))}
                              </select>
                            }
                          </td>
                          <td></td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ))}
      </div>
      <button onClick={(e) => handleSubmit(e)} className="submit-btn">
        Submit
      </button>
      <ToastContainer />
    </div>
  );
};

export default ConvertToProject;
